/**
 * Task Manager Card
 * Displays task statistics and completion rate
 */
import React from 'react';
import PropTypes from 'prop-types';
import { ListTodo, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../contexts/PermissionContext';

const TaskManagerCard = ({ taskStats }) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // Don't show card if viewer doesn't have permission
  if (!hasPermission('can_view_tasks')) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-cyan-600 to-sky-600 p-4 text-white">
        <div className="flex items-center space-x-3">
          <ListTodo size={24} />
          <div>
            <h3 className="text-lg font-bold">Task Manager</h3>
            <p className="text-xs text-cyan-100">Action Items</p>
          </div>
        </div>
      </div>
      {taskStats && taskStats.total > 0 ? (
        <div className="p-4 space-y-2">
          {/* Completion Rate - Prominent Display */}
          <div className="text-center p-2.5 bg-gradient-to-r from-cyan-50 to-sky-50 border border-cyan-200 rounded-lg">
            <p className="text-2xl font-bold text-cyan-600">{taskStats.completion_rate}%</p>
            <p className="text-xs text-gray-600 mt-0.5">Completion Rate</p>
          </div>

          {/* Task Status Grid - 3 columns, 2 rows */}
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="text-center p-2 bg-gray-50 rounded">
              <p className="text-lg font-bold text-gray-600">{taskStats.not_started}</p>
              <p className="text-xs text-gray-600 mt-0.5">Not Started</p>
            </div>
            <div className="text-center p-2 bg-blue-50 rounded">
              <p className="text-lg font-bold text-blue-600">{taskStats.in_progress}</p>
              <p className="text-xs text-gray-600 mt-0.5">In Progress</p>
            </div>
            <div className="text-center p-2 bg-purple-50 rounded">
              <p className="text-lg font-bold text-purple-600">{taskStats.under_review}</p>
              <p className="text-xs text-gray-600 mt-0.5">Under Review</p>
            </div>
            <div className="text-center p-2 bg-green-50 rounded">
              <p className="text-lg font-bold text-green-600">{taskStats.completed}</p>
              <p className="text-xs text-gray-600 mt-0.5">Completed</p>
            </div>
            <div className="text-center p-2 bg-orange-50 rounded">
              <p className="text-lg font-bold text-orange-600">{taskStats.not_relevant || 0}</p>
              <p className="text-xs text-gray-600 mt-0.5">Not Relevant</p>
            </div>
            <div className="text-center p-2 bg-red-50 rounded">
              <p className="text-lg font-bold text-red-600">{taskStats.overdue || 0}</p>
              <p className="text-xs text-gray-600 mt-0.5">Overdue</p>
            </div>
          </div>

          {/* Spacer to align button with other widgets */}
          <div className="h-2"></div>

          <button
            onClick={() => navigate('/task-manager')}
            className="w-full px-4 py-2.5 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 font-semibold transition text-sm flex items-center justify-center space-x-2 min-h-[44px]"
          >
            <span>Manage Tasks</span>
            <ChevronRight size={16} />
          </button>
        </div>
      ) : (
        <div className="p-4">
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 mb-3">No tasks yet</p>
            <button
              onClick={() => navigate('/task-manager')}
              className="w-full px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 font-semibold transition text-sm min-h-[44px]"
            >
              View Tasks
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

TaskManagerCard.propTypes = {
  taskStats: PropTypes.shape({
    total: PropTypes.number,
    completion_rate: PropTypes.number,
    not_started: PropTypes.number,
    in_progress: PropTypes.number,
    under_review: PropTypes.number,
    completed: PropTypes.number,
    not_relevant: PropTypes.number,
    overdue: PropTypes.number
  })
};

export default TaskManagerCard;
